import React, { useContext, useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { Web3Context } from '../../context/Web3Context';
import { PACKAGE_NAMES, PACKAGE_PRICES, RETOPUP_PRICES, CEILING_LIMITS, PACKAGE_COLORS } from '../../utils/constants';
import './Packages.css';

function SpeedoGauge({ percent, color, size = 80 }) {
  const r = (size - 10) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const startAngle = 135;
  const endAngle = 405;
  const totalArc = endAngle - startAngle;
  const filledAngle = startAngle + (totalArc * Math.min(percent, 100)) / 100;

  function polarToCart(angle, radius) {
    const rad = (angle * Math.PI) / 180;
    return { x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) };
  }

  const bgStart = polarToCart(startAngle, r);
  const bgEnd = polarToCart(endAngle, r);
  const fillEnd = polarToCart(filledAngle, r);
  const largeArcBg = totalArc > 180 ? 1 : 0;
  const filledArc = filledAngle - startAngle;
  const largeArcFill = filledArc > 180 ? 1 : 0;

  const gaugeColor = percent >= 100 ? '#ef4444' : percent >= 75 ? '#f59e0b' : color;
  const needle = polarToCart(filledAngle, r - 8);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <path d={`M${bgStart.x},${bgStart.y} A${r},${r} 0 ${largeArcBg},1 ${bgEnd.x},${bgEnd.y}`}
        fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="6" strokeLinecap="round" />
      {percent > 0 && (
        <path d={`M${bgStart.x},${bgStart.y} A${r},${r} 0 ${largeArcFill},1 ${fillEnd.x},${fillEnd.y}`}
          fill="none" stroke={gaugeColor} strokeWidth="6" strokeLinecap="round"
          style={{ filter: `drop-shadow(0 0 4px ${gaugeColor}60)`, transition: 'all 0.8s ease' }} />
      )}
      <circle cx={needle.x} cy={needle.y} r="3" fill={gaugeColor}
        style={{ filter: `drop-shadow(0 0 6px ${gaugeColor})`, transition: 'all 0.8s ease' }} />
      <text x={cx} y={cy + 2} textAnchor="middle" fill="#fff" fontSize="14" fontWeight="800">
        {Math.round(percent)}%
      </text>
      <text x={cx} y={cy + 14} textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="8">
        Ceiling
      </text>
    </svg>
  );
}

export default function Packages({ notify }) {
  const { userId, account, contracts, readContracts } = useContext(Web3Context);
  const [packages, setPackages] = useState([]);
  const [ceilingPercents, setCeilingPercents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    if (!userId || !readContracts.views) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const ceilingCalls = [];
        for (let i = 0; i < 7; i++) {
          ceilingCalls.push(readContracts.core.checkCeilingAvailable(userId, i, ethers.constants.MaxUint256).catch(() => [false, ethers.BigNumber.from(0), ethers.BigNumber.from(0)]));
        }
        const [info, ...results] = await Promise.all([
          readContracts.views.getUserInfo(userId),
          ...ceilingCalls
        ]);
        if (cancelled) return;
        const pkgActive = info.packageActive || [];
        setPackages(pkgActive);
        const percents = results.map((res, i) => {
          if (!pkgActive[i]) return 0; // inactive package = 0% (grey gauge)
          const limitWei = ethers.utils.parseUnits(CEILING_LIMITS[i].toString(), 18);
          const remaining = res[1];
          const used = limitWei.sub(remaining.gt(limitWei) ? limitWei : remaining);
          return limitWei.gt(0) ? used.mul(10000).div(limitWei).toNumber() / 100 : 0;
        });
        setCeilingPercents(percents);
      } catch (err) {
        if (!cancelled) notify.error('Failed to load packages');
      }
      if (!cancelled) setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [userId, readContracts.views]);

  async function approveUSDT(amount) {
    const amountWei = ethers.utils.parseUnits(amount.toString(), 18);
    const allowance = await readContracts.usdt.allowance(account, contracts.core.address);
    if (allowance.gte(amountWei)) return true;
    const loadId = notify.loading('Approving USDT...');
    try {
      const tx = await contracts.usdt.approve(contracts.core.address, amountWei);
      await tx.wait();
      notify.removeNotification(loadId);
      notify.success('USDT Approved');
      return true;
    } catch (err) {
      notify.removeNotification(loadId);
      notify.error('Approval failed: ' + (err.reason || err.message));
      return false;
    }
  }

  async function handleActivate(pkgId) {
    setProcessing(pkgId);
    try {
      const approved = await approveUSDT(PACKAGE_PRICES[pkgId]);
      if (!approved) { setProcessing(null); return; }
      const loadId = notify.loading(`Activating ${PACKAGE_NAMES[pkgId]}...`);
      const tx = await contracts.core.activatePackage(pkgId);
      await tx.wait();
      notify.removeNotification(loadId);
      notify.success(`${PACKAGE_NAMES[pkgId]} activated!`);
      setPackages(prev => { const n = [...prev]; n[pkgId] = true; return n; });
    } catch (err) { notify.error('Activation failed: ' + (err.reason || err.message)); }
    setProcessing(null);
  }

  async function handleRetopup(pkgId) {
    setProcessing('retopup-' + pkgId);
    try {
      const approved = await approveUSDT(RETOPUP_PRICES[pkgId]);
      if (!approved) { setProcessing(null); return; }
      const loadId = notify.loading(`Re-topping up ${PACKAGE_NAMES[pkgId]}...`);
      const tx = await contracts.core.retopup(pkgId);
      await tx.wait();
      notify.removeNotification(loadId);
      notify.success(`${PACKAGE_NAMES[pkgId]} re-topped up!`);
    } catch (err) { notify.error('Re-topup failed: ' + (err.reason || err.message)); }
    setProcessing(null);
  }

  if (loading) {
    return <div className="page-loading"><div className="mini-spinner" /><span>Loading packages...</span></div>;
  }

  return (
    <div className="packages">
      <h2 className="page-title">Packages</h2>
      <div className="pkg-list">
        {PACKAGE_NAMES.map((name, i) => {
          const active = packages[i];
          const canActivate = !active && (i === 0 || packages[i - 1]);
          const pct = ceilingPercents[i] || 0;
          return (
            <div key={i} className="pkg-card animate-in" style={{ animationDelay: `${i * 0.05}s`, borderColor: active ? PACKAGE_COLORS[i] + '40' : 'rgba(255,255,255,0.06)' }}>
              <div className="pkg-top">
                <div className="pkg-left">
                  <div className="pkg-icon" style={{ background: PACKAGE_COLORS[i] + '20', color: PACKAGE_COLORS[i] }}>{i + 1}</div>
                  <div className="pkg-info">
                    <span className="pkg-name">{name}</span>
                    <span className="pkg-price">${PACKAGE_PRICES[i]} USDT</span>
                    <div className="pkg-detail-tags">
                      <span className="pkg-tag">Ceiling: ${CEILING_LIMITS[i]}</span>
                      <span className="pkg-tag">Retopup: ${RETOPUP_PRICES[i]}</span>
                    </div>
                  </div>
                </div>
                <div className="pkg-gauge">
                  <SpeedoGauge percent={pct} color={PACKAGE_COLORS[i]} size={76} />
                </div>
              </div>
              <div className="pkg-actions">
                {canActivate && (
                  <button className="action-btn action-btn-primary" onClick={() => handleActivate(i)} disabled={processing === i}>
                    {processing === i ? 'Processing...' : `Activate $${PACKAGE_PRICES[i]}`}
                  </button>
                )}
                {active && (
                  <button className="action-btn action-btn-secondary" onClick={() => handleRetopup(i)} disabled={processing === 'retopup-' + i}>
                    {processing === 'retopup-' + i ? 'Processing...' : `Re-topup $${RETOPUP_PRICES[i]}`}
                  </button>
                )}
                {!active && !canActivate && (
                  <span className="pkg-locked-text">Activate previous package first</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
